import apiService from './api';
import locationService from './location';
import { canUseLocationSilently } from './locationGate';
import { unwrap } from '../utils/apiResponse';

/**
 * Presence pings (harvest & labour plan §3a): about every 10 minutes, one
 * position from an app user whose own time is open on a job whose type of work
 * asks for it. The server moves their time to the cultivar area two pings in a
 * row agree on.
 *
 * THIS VERSION PINGS ONLY WHILE THE APP IS OPEN — on start, when the app comes
 * back to the foreground, and on a 10-minute timer while it stays open. That
 * costs no background battery at all; a picker who opens the app to start a
 * bin still gets placed. Background pinging through the native GPS plugin
 * waits for the on-device battery test the plan calls for.
 *
 * It never prompts: without the location disclosure already accepted (clock-in
 * shows it) it does nothing.
 */

const QUEUE_KEY = 'factree_presence_queue_v1';
const LAST_KEY = 'factree_presence_last_ping';
const MIN_GAP_MS = 9 * 60 * 1000;
const MAX_QUEUE = 50;

interface Ping { lat: number; lng: number; accuracy: number | null; recorded_at: string }

function readQueue(): Ping[] {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]') as Ping[]; } catch { return []; }
}
function writeQueue(q: Ping[]) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_QUEUE))); } catch { /* storage blocked */ }
}
function lastPingAt(): number {
  try { return Number(localStorage.getItem(LAST_KEY) || 0); } catch { return 0; }
}
function setLastPingAt(t: number) {
  try { localStorage.setItem(LAST_KEY, String(t)); } catch { /* storage blocked */ }
}

let busy = false;

async function flush(): Promise<void> {
  const q = readQueue();
  if (!q.length) return;
  try {
    unwrap(await apiService.post('/field-work/me/presence', { pings: q }));
    writeQueue([]);
  } catch (e) {
    const err = e as { offline?: boolean; status?: number };
    // Keep them for next time on a network failure; drop on a refusal (not on
    // the roster, a ping outside the accepted window) so a bad batch cannot
    // block every later one.
    if (!(err?.offline || err?.status == null)) writeQueue([]);
  }
}

/**
 * Take a ping if one is wanted and due. Safe to call often — it rate-limits
 * itself and never runs twice at once.
 */
export async function pingIfWanted(force = false): Promise<void> {
  if (busy) return;
  busy = true;
  try {
    await flush();
    if (!canUseLocationSilently()) return;
    if (!force && Date.now() - lastPingAt() < MIN_GAP_MS) return;

    const res = await apiService.get<{ wanted: boolean }>('/field-work/me/presence');
    if (!unwrap(res).wanted) return;

    const pos = await locationService.getCurrentPosition({ enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 });
    setLastPingAt(Date.now());
    writeQueue([...readQueue(), {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
      recorded_at: new Date(pos.timestamp || Date.now()).toISOString(),
    }]);
    await flush();
  } catch {
    // No fix, no signal, no licence: a missed ping is harmless, the next one comes.
  } finally {
    busy = false;
  }
}

export const PRESENCE_INTERVAL_MS = 10 * 60 * 1000;
